// v0.6.8 — extracted from CalculatePanel.jsx (500-LOC cap) for the
// filing-period/frequency mismatch warning. A client's declared filing
// frequency (UK VAT, per-country local returns) was saved and shown in
// Settings but never checked against the period actually picked at
// calc time — operator-flagged missing correlation. Initially scoped
// to only warn on a MONTHLY mismatch (a quarterly filer running a
// shorter interim-check period seemed like normal, unflagged
// behavior) — but per follow-up operator instruction, the declared
// frequency on the VAT registration is the authoritative reference:
// EVERY mismatch should surface, including a monthly filer running a
// quarter-length calc or vice versa, not just the monthly-specific
// case.

// NB: explicit ".js" — webpack resolves either form, but the node
// test harness (frontend/tests/*.test.mjs) is real ESM and will not
// resolve an extensionless relative import. Dropping it breaks the
// tests only, not the build, which is the more dangerous direction.
import { cadencesFor, classifyStatutoryPeriod } from "./filingPeriods.js";

const DAY_MS = 86400000;

// Tolerance windows a period's actual length (inclusive of both ends)
// must fall inside to count as matching that cadence. Generous enough
// to cover 28-31-day months, 90-92-day quarters, and 365/366-day years
// without false positives from ordinary calendar variation.
const SHAPE_RANGES = {
  MONTHLY: [27, 31],
  QUARTERLY: [89, 92],
  ANNUALLY: [359, 366],
};

/** Inclusive day count of [periodStart, periodEnd], or 0 if either is
 * unset. Exported for callers that need the raw length, not just the
 * MONTHLY/QUARTERLY/ANNUALLY classification below. */
export function periodDayCount(periodStart, periodEnd) {
  if (!periodStart || !periodEnd) return 0;
  return Math.round((new Date(periodEnd) - new Date(periodStart)) / DAY_MS) + 1;
}

// v0.6.8 — mirrors the backend's engine/filing_cadence_guard.py hard
// bound exactly: no legitimate calc of any kind (final filing or
// interim preview) ever needs a period longer than a year. Found live:
// the auto-detect-period-from-selected-runs feature silently produced
// a multi-year span with nothing to stop it.
export const MAX_CALC_PERIOD_DAYS = 366;

/** Which of MONTHLY/QUARTERLY/ANNUALLY the period's length matches,
 * or null if it doesn't cleanly match any of them (a custom/partial
 * range). */
function classifyPeriodShape(periodStart, periodEnd) {
  const days = periodDayCount(periodStart, periodEnd);
  for (const [shape, [lo, hi]] of Object.entries(SHAPE_RANGES)) {
    if (days >= lo && days <= hi) return shape;
  }
  return null;
}

// IOSS and OSS (Union/Non-Union) cadence is mandated by EU law, not an
// operator choice — IOSS returns are always monthly (Article 369s),
// OSS Union and Non-Union returns are always quarterly (Article
// 369f/369). There's no per-client "declared frequency" for these to
// read (the client-form Frequency dropdown only shows for LOCAL rows,
// correctly, since it's nothing to configure) — the reference here is
// the fixed legal cadence itself, not a Settings default.
const FIXED_SCHEME_CADENCE = {
  IOSS: "MONTHLY",
  OSS_UNION: "QUARTERLY",
  OSS_NON_UNION: "QUARTERLY",
  OSS: "QUARTERLY", // legacy bare type, same treatment filing_cadence.py already gives it
};
const SCHEME_LABEL = {
  IOSS: "IOSS", OSS_UNION: "OSS Union", OSS_NON_UNION: "OSS Non-Union", OSS: "OSS",
};

/** Every registration whose filing cadence doesn't match the selected
 * period's actual length — LOCAL rows against their declared
 * frequency (Monthly/Quarterly/Annually, defaulting to QUARTERLY when
 * unset, same default the backend and client form already use), and
 * IOSS/OSS rows against their fixed legal cadence. Each entry:
 * {label, declared}. */
export function frequencyMismatches(client, periodStart, periodEnd) {
  if (!periodStart || !periodEnd) return [];
  // v0.6.10 — a client filing on MORE THAN ONE cadence made this warn
  // on every period it was ever given, because no single period can
  // satisfy both a monthly and a quarterly registration at once. With
  // the Step-3 picker now offering statutory periods, that meant the
  // app proposed Q1 2025 and then warned that Q1 2025 was wrong.
  //
  // A period that IS a whole statutory period for one of the cadences
  // this client files on is the correct period for those returns. The
  // registrations on other cadences are not a mismatch — they simply
  // file on their own periods, in their own runs. Warn only when the
  // period is a return period for NONE of them, which is the case
  // that actually needs the operator's attention (a hand-typed range,
  // or a fiscal year for a client with no annual return).
  const statutory = classifyStatutoryPeriod(periodStart, periodEnd);
  if (statutory
      && cadencesFor(client).some((c) => c.cadence === statutory)) {
    return [];
  }
  const shape = classifyPeriodShape(periodStart, periodEnd);
  const regs = client?.company_vat_numbers || [];
  const out = [];
  const check = (label, declared) => {
    if (declared !== shape) out.push({ label, declared });
  };

  const localCcs = regs
    .filter((e) => (e.registration_type || "LOCAL").toUpperCase() === "LOCAL")
    .map((e) => (e.country || e.country_code || "").toUpperCase())
    .filter(Boolean);
  if (localCcs.includes("GB")) {
    check("GB (UK VAT)", (client?.uk_vat_frequency || "QUARTERLY").toUpperCase());
  }
  const localFreq = client?.local_return_frequency || {};
  for (const cc of new Set(localCcs.filter((c) => c !== "GB"))) {
    check(cc, (localFreq[cc] || "QUARTERLY").toUpperCase());
  }

  const seenSchemes = new Set();
  for (const e of regs) {
    const rt = (e.registration_type || "").toUpperCase();
    const fixed = FIXED_SCHEME_CADENCE[rt];
    if (!fixed) continue;
    const cc = (e.country || e.country_code || "").toUpperCase();
    const key = `${rt}:${cc}`;
    if (seenSchemes.has(key)) continue;
    seenSchemes.add(key);
    check(`${cc || "?"} (${SCHEME_LABEL[rt]})`, fixed);
  }
  return out;
}

/** A2-537 — the registrations that file on a DIFFERENT cadence from a
 * whole statutory period this client files on. frequencyMismatches stays
 * silent for them on purpose (v0.6.10: they file on their own periods, in
 * their own runs), but the screen said nothing, so a quarterly calc
 * listing CZ and IOSS (monthly) read as if it covered them. Empty when the
 * period is not one of this client's return periods — that case is the
 * mismatch warning's. Each entry: {cadence, labels}. */
export function filedOnOtherPeriods(client, periodStart, periodEnd) {
  const statutory = classifyStatutoryPeriod(periodStart, periodEnd);
  const cadences = cadencesFor(client);
  if (!statutory || !cadences.some((c) => c.cadence === statutory)) return [];
  return cadences.filter((c) => c.cadence !== statutory)
    .map(({ cadence, labels }) => ({ cadence, labels }));
}
